import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConversationEntry as ConversationEntryType, markEntryAsOld, updateQuestion, updateEntryFeedback, EntryFeedback } from '../store/conversationSlice';
import QuestionEditModal from './QuestionEditModal';
import FeedbackPopover from './FeedbackPopover';
import { ConversationCategory } from '../types/conversation';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';
import EditIcon from '@mui/icons-material/Edit';
import EarlierTag from './EarlierTag';
import { AnswerText, CategoryChip } from './InsightCardParts';
import { useIsWatcher } from '../contexts/SessionViewerContext';

interface ConversationEntryProps {
  entry: ConversationEntryType;
}

const ConversationEntry: React.FC<ConversationEntryProps> = ({ entry }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  // A watcher reads the owner's cards; feedback and edits write to the owner's session.
  const isWatcher = useIsWatcher();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'stored' | 'ai'>('stored');
  // Collapse answers for NEW_INQUIRY by default to reduce UI clutter
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(
    entry.category !== ConversationCategory.NEW_INQUIRY
  );

  // Mark entry as old after 3 seconds to remove the highlight
  useEffect(() => {
    if (entry.isNew) {
      const timer = setTimeout(() => {
        dispatch(markEntryAsOld(entry.id));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [entry.isNew, entry.id, dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success';
      case 'pending':
        return 'bg-warning';
      case 'error':
        return 'bg-error';
      default:
        return 'bg-ink-disabled';
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour12: false, 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    } catch {
      return 'Invalid time';
    }
  };

  const handleQuestionEdited = (_originalQuestion: string, editedQuestion: string, answer: string) => {
    dispatch(updateQuestion({ id: entry.id, editedQuestion, answer }));
  };

  const handleFeedbackSubmit = async (rating: 'up' | 'down', reason: string) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so the UI reflects the choice even if persist fails
    const feedback: EntryFeedback = {
      rating,
      reason: reason || undefined,
      submittedAt: new Date().toISOString()
    };
    dispatch(updateEntryFeedback({ id: entry.id, feedback }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'conversation',
        entryTimestamp: entry.timestamp,
        category: entry.category,
        rating,
        reason
      });
    } catch (err) {
      // Persistence failed — feedback stays in Redux (will be included in session save)
      console.warn('[ConversationEntry] Entry feedback persist failed:', err);
    }
  };


  // The answer box: paper, radius 8, padding 10, at least 80 tall (macOS AnswerTabView).
  const answerBox = 'min-h-[80px] rounded-control bg-paper p-2.5';
  // An empty or waiting answer: one centred caption in the same box.
  const answerNote = 'flex min-h-[80px] items-center justify-center rounded-control bg-paper px-2.5 py-3 text-center text-[11px] text-ink-muted';
  const segment = (isActive: boolean) =>
    `inline-flex items-center gap-1 whitespace-nowrap rounded-inner px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
      isActive ? 'bg-surface text-signature' : 'text-ink-secondary hover:text-ink'
    }`;
  const tint = (token: string, percent: number) => `color-mix(in srgb, var(${token}) ${percent}%, transparent)`;

  // The macOS insight card: white, radius 16, padding 12, blocks 8 apart, no border.
  return (
    <div className={`insight-card space-y-2 rounded-card p-3 ${entry.isNew ? 'insight-arrive' : 'bg-surface'}`}>
      {/* Header: time (and "Earlier"), then feedback and the status dot */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium text-ink-muted">{formatTime(entry.timestamp)}</span>
        {entry.isRestored && <EarlierTag />}
        <span className="flex-1" />
        {!isWatcher && (
          <FeedbackPopover
            currentFeedback={entry.feedback}
            onSubmit={handleFeedbackSubmit}
            disabled={entry.status === 'pending'}
          />
        )}
        <div className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${getStatusColor(entry.status)}`} />
      </div>

      {/* Customer Question */}
      <div className="flex items-start gap-2 rounded-control bg-paper p-2.5">
        <p className="min-w-0 flex-1 text-sm font-bold text-ink">{entry.question}</p>
        {!isWatcher && (
          <button
            type="button"
            onClick={() => setIsEditModalOpen(true)}
            title="Edit question"
            aria-label="Edit question"
            className="inline-flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-full bg-signature text-on-signature transition-colors hover:bg-signature-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signature"
          >
            <EditIcon sx={{ fontSize: 10 }} />
          </button>
        )}
      </div>

      {/* Category and Relevance */}
      <div className="flex items-center justify-between gap-2">
        <CategoryChip category={entry.category} />
        <div className="text-[11px] text-ink-secondary">
          Relevance: <span className="font-semibold text-ink">{entry.relevance_score.toFixed(2)}</span>
        </div>
      </div>

      {/* Expand/Collapse Toggle for NEW_INQUIRY */}
      {entry.category === ConversationCategory.NEW_INQUIRY && (
        <button
          onClick={() => setIsAnswerExpanded(!isAnswerExpanded)}
          className="w-full flex items-center justify-center gap-2 py-1.5 text-xs font-medium text-ink-muted hover:text-signature hover:bg-signature-tint rounded-control transition-all border border-hairline"
        >
          <span className="text-base">{isAnswerExpanded ? '▼' : '▶'}</span>
          <span>{isAnswerExpanded ? 'Hide Answers' : 'Show Answers'}</span>
          {!isAnswerExpanded && (
            <span className="ml-1 flex items-center gap-1">
              {entry.stored_answer && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-inner bg-info-tint text-info text-xs">
                  Stored
                </span>
              )}
              {entry.status === 'completed' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-inner bg-success-tint text-success text-xs">
                  AI
                </span>
              )}
            </span>
          )}
        </button>
      )}

      {/* Answer Section with Tabs */}
      {isAnswerExpanded && (
      <div className="overflow-hidden rounded-control border border-hairline bg-surface">
        {/* Tab Headers — a segmented control flush in the container's top-left corner,
            sized to its own content, so it reads clearly as a tab switcher */}
        <div className="inline-flex gap-1 rounded-control bg-paper p-[3px]">
          <button onClick={() => setActiveTab('stored')} className={segment(activeTab === 'stored')}>
            Stored Answer
            {entry.stored_answer && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-success"></span>
            )}
          </button>
          <button onClick={() => setActiveTab('ai')} className={segment(activeTab === 'ai')}>
            AI Answer
            {entry.status === 'completed' && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-success"></span>
            )}
            {entry.status === 'pending' && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning animate-pulse"></span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-2.5">
          {activeTab === 'stored' ? (
            /* Stored Answer Tab */
            <div>
              {entry.stored_answer ? (
                <div className={`${answerBox} text-sm text-ink`}>
                  <AnswerText answer={entry.stored_answer} />
                </div>
              ) : entry.category === ConversationCategory.NEW_INQUIRY ? (
                <div className={answerNote}>
                  No stored answer - this is a new objection/FAQ not yet in the knowledge base
                </div>
              ) : entry.status !== 'pending' ? (
                /* Settled entries (completed or error, incl. restored
                   sessions) will never get a late stored_answer — don't
                   spin forever. */
                <div className={answerNote}>
                  No stored answer was found for this question
                </div>
              ) : (
                <div className={answerNote}>
                  Loading stored answer...
                </div>
              )}
            </div>
          ) : entry.status === 'pending' ? (
            /* AI Answer Tab: still working */
            <div
              className="flex min-h-[80px] flex-col items-center justify-center gap-2 rounded-control border py-4"
              style={{ backgroundColor: tint('--warning', 10), borderColor: tint('--warning', 30) }}
            >
              <div className="w-4 h-4 border-2 border-warning border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-ink-secondary">AI is processing your question...</span>
            </div>
          ) : entry.status === 'error' ? (
            <div
              className="min-h-[80px] rounded-control border p-2.5 text-sm text-error"
              style={{ backgroundColor: tint('--error', 10), borderColor: tint('--error', 30) }}
            >
              {entry.error || 'An error occurred while generating the response'}
            </div>
          ) : (
            <div className={`${answerBox} text-sm text-ink`}>
              {entry.answer && <AnswerText answer={entry.answer} />}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Edit Question Modal */}
      <QuestionEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        originalQuestion={entry.question}
        cardKey={entry.cardKey}
        cardTimestamp={entry.timestamp}
        onQuestionEdited={handleQuestionEdited}
      />
    </div>
  );
};

export default ConversationEntry;
